import React from 'react';

/**
 * Renders a motorcycle's plate(s) as coloured "badges":
 *  - Old plate → white background, black text, thin black border (classic B&W plate).
 *  - Golden plate → warm yellow background, dark text (new UAE golden plate).
 *
 * If only the old plate exists, renders just that badge.
 * If a golden plate is present, renders BOTH side-by-side (old + golden as a pair).
 *
 * Usage:
 *   <PlatePair oldPlate={m.plate_number} goldenPlate={m.golden_plate_number} />
 *   <PlatePair oldPlate={f.bike_plate_number} goldenPlate={f.bike_golden_plate_number} compact />
 */
const PlatePair = ({ oldPlate, goldenPlate, compact = false, className = '' }) => {
  const hasOld = !!(oldPlate && String(oldPlate).trim() && oldPlate !== '-');
  const hasGolden = !!(goldenPlate && String(goldenPlate).trim() && goldenPlate !== '-');

  if (!hasOld && !hasGolden) {
    return <span className="text-slate-400">-</span>;
  }

  const basePlate = `inline-flex items-center font-mono font-semibold rounded-sm border ${
    compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'
  } tracking-tight whitespace-nowrap`;

  return (
    <div className={`inline-flex items-center gap-1.5 flex-wrap ${className}`} data-testid="plate-pair">
      {hasOld && (
        <span
          className={`${basePlate} bg-white text-black border-black`}
          title="Old plate (Black & White)"
          data-testid="plate-old"
        >
          {oldPlate}
        </span>
      )}
      {hasGolden && (
        <span
          className={`${basePlate} bg-yellow-300 text-black border-yellow-600`}
          title="Golden plate (new)"
          data-testid="plate-golden"
        >
          {goldenPlate}
        </span>
      )}
    </div>
  );
};

export default PlatePair;
export { PlatePair };
